import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppDispatch } from '../../../../hooks/redux';
import { userInfoApi } from '../../../../services/apiServices/user/userInfo';
import { ButtonClassNames, ModalVariants, InputTextFormat, InputRegexPattern } from '../../../../models/Common';
import { ApiError } from '../../../../models/ApiError';
import { FormInputText } from '../../../inputs/FormInputText';
import { FormInputPassword } from '../../../inputs/FormInputPassword';
import { CustomMUIButton } from '../../buttons/CustomMUIButton';
import { modalSlice } from '../../../../store/reducers/ModalSlice';
import { personalAccountRulesDocLink } from '../../../../constants/configuration';

export const LinkAccount: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { openModal } = modalSlice.actions;
  const [linkAccountCallback, { isLoading }] = userInfoApi.useLinkFamilyAccountMutation();

  const methods = useForm({
    defaultValues: {
      contractNumber: '',
      currentPassword: '',
    },
  });
  const { handleSubmit, setError } = methods;

  const handleSendRequest = async ({
    contractNumber,
    currentPassword,
  }: {
    contractNumber: string;
    currentPassword: string;
  }) => {
    try {
      const res = await linkAccountCallback({ userId: contractNumber, password: currentPassword }).unwrap();
      const description =
        res.Invite.NotifyChannels.sms || res.Invite.NotifyChannels.email ? (
          <>
            {t('multiAccount.linkWasSent')}
            <br />
            <br />
            <span style={{ color: 'var(--color-error)' }}>
              {t('multiAccount.linkIsValidFor', { min: res.Invite.ttl })}
            </span>
            <br />
            <br />
            <span style={{ fontSize: '14px' }}>{t('multiAccount.confirmConnection')}</span>
          </>
        ) : (
          t('multiAccount.mustConfirm')
        );
      dispatch(
        openModal({
          modalVariants: ModalVariants.Success,
          content: {
            title: `${t('success.balanceHasBeenReplenishedSuccessfully')}`,
            description: description as string,
          },
        }),
      );
    } catch (err: unknown) {
      const error = err as ApiError;
      setError('contractNumber', {
        type: 'server',
        message: error?.data?.error?.message,
      });
    }
  };

  return (
    <div className="modals">
      <div className="modals__header">
        <article className="modals__header__title">{t('multiAccount.joinTheBrizAccount')}</article>
        <div className="modals__warning">
          {t('multiAccount.enterContractNumberAndPassword')}
          <br />
          <br />
          {t('multiAccount.ownerWillReceiveNotification')}
        </div>
      </div>
      <FormProvider {...methods}>
        <form className="modals__form" onSubmit={handleSubmit(handleSendRequest)}>
          <FormInputText
            name="contractNumber"
            label={t('terminateContract.accountNumber')}
            className="primary"
            isRequired
            minLength={4}
            format={InputTextFormat.ContractNumber}
            pattern={InputRegexPattern.OnlyNumbers}
          />
          <FormInputPassword
            name="currentPassword"
            className="primary"
            label={t('common.password')}
            isRequired
            minLength={6}
            maxLength={16}
            autoComplete="new-password"
            fieldHasName
          />
          <div className="modals__form__agreement-label">
            {t('multiAccount.agreeToRulesOf')}{' '}
            <a href={personalAccountRulesDocLink} target="_blank" rel="noreferrer">
              {t('multiAccount.termsOfService')}
            </a>
            .
          </div>
          <div className="modals__btn-group">
            <CustomMUIButton
              label={t('multiAccount.addAccount')}
              type="submit"
              className={ButtonClassNames.PrimaryFulfilled}
              isBold
              isLoading={isLoading}
            />
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
